/**
 * Contact Form Models for user-management-lib
 *
 * @remarks
 * These models map directly to the two contact endpoints:
 * - `POST /api/contact`           — public, no authentication required
 * - `POST /api/contact/org/:orgId` — authenticated, targets org Admin members
 *
 * The backend applies express-validator rules whose constraints are mirrored
 * here as JSDoc `@remarks` so consumers can build matching reactive forms.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Request models
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Payload sent to either contact endpoint.
 *
 * @remarks
 * **Server-side validation constraints:**
 * | Field     | Rule                                 |
 * |-----------|--------------------------------------|
 * | `name`    | required · string · 2–100 characters |
 * | `email`   | required · valid e-mail address      |
 * | `subject` | required · string · 5–100 characters |
 * | `message` | required · string · 20–2000 characters |
 */
export interface ContactPayload {
  /** Sender's full name (2–100 chars). */
  name: string;
  /** Sender's e-mail address. */
  email: string;
  /** Message subject (5–100 chars). */
  subject: string;
  /** Message body (20–2000 chars). */
  message: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Response models
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Success response returned by both contact endpoints.
 *
 * @remarks
 * HTTP 200 is returned on success — the body only guarantees `success: true`.
 * The client does not receive information about how many recipients were notified.
 */
export interface ContactResponse {
  /** Always `true` on a successful submission. */
  success: boolean;
}

/**
 * Structured error thrown by {@link ContactService} on request failure.
 *
 * @remarks
 * The service catches raw `HttpErrorResponse` objects and re-throws a typed
 * error so view components never need to inspect raw HTTP internals.
 *
 * | `status` | Scenario                                          |
 * |----------|---------------------------------------------------|
 * | `0`      | Network error / no connection                     |
 * | `400`    | Validation failure — `fieldErrors` may be present |
 * | `401`    | Unauthenticated (org endpoint only)               |
 * | `403`    | Not a member of the target org (org endpoint only)|
 * | `404`    | Org not found or has no Admin members             |
 * | `429`    | Rate limit exceeded — `retryAfterSeconds` set     |
 * | `5xx`    | Server-side error                                 |
 */
export interface ContactError {
  /** Human-readable error message safe to display in UI. */
  message: string;
  /** HTTP status code. `0` indicates a network error. */
  status: number;
  /** Name of the service operation that failed. */
  operation: string;
  /**
   * Field-level validation messages returned by the API for HTTP 400 responses.
   * Each entry represents one failed constraint.
   */
  fieldErrors?: string[];
  /**
   * Seconds the consumer must wait before the rate limit resets (HTTP 429).
   * Derived from the `Retry-After` response header when present.
   */
  retryAfterSeconds?: number;
  /** Raw HTTP error for advanced consumers that need the original response. */
  originalError?: unknown;
}
